import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import type { DaaluuAction, GameView } from '@big2/shared';
import { MixingHand } from './DaaluuShuffleTable.js';

interface Props { game: GameView; mn: boolean; disabled: boolean; onCollect: (action: DaaluuAction) => void }
interface Point { x: number; y: number }
interface Flight { serial: number; from: Point; to: Point; count: number; angle: number }

function BackStack({ count }: { count: number }) {
  return <span className="daaluu-distribution-stack" aria-hidden="true">{Array.from({ length: count }, (_, layer) =>
    <span className="daaluu-distribution-back" key={layer} style={{ '--back-layer': layer } as CSSProperties}></span>)}
  </span>;
}

/** Pointer motion is local; only a completed collection requests a server transition. */
export function DaaluuDistribution({ game, mn, disabled, onCollect }: Props) {
  const d = game.daaluu!, distribution = d.distribution;
  const helpId = useId();
  const collecting = d.stage === 'selecting' || d.stage === 'distributing';
  const buttons = useRef(new Map<number, HTMLButtonElement>());
  const drag = useRef<{ index: number; pointerId: number; element: HTMLButtonElement; startX: number; startY: number } | null>(null);
  const suppressClick = useRef(false);
  const pending = useRef(false);
  const dropped = useRef<{ index: number; point: Point } | null>(null);
  const seen = useRef({ roundId: d.shuffle.roundId, serial: distribution.collected });
  const geometry = useRef({ sources: new Map<number, Point>(), targets: new Map<number, Point>() });
  const [pointer, setPointer] = useState<(Point & { index: number }) | null>(null);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [flight, setFlight] = useState<Flight | null>(null);
  const nextIndex = distribution.startIndex === null ? null : (distribution.startIndex + distribution.collected) % d.shuffle.stacks.length;
  const allowed = (index: number) => collecting && !disabled && (nextIndex === null || nextIndex === index);

  function release() {
    const current = drag.current;
    drag.current = null; setPointer(null);
    if (current?.element.hasPointerCapture?.(current.pointerId)) current.element.releasePointerCapture(current.pointerId);
    if (current && document.activeElement === current.element) current.element.blur();
  }

  useLayoutEffect(() => {
    if (!collecting) return;
    const snapshot = () => {
      buttons.current.forEach((button, index) => {
        const rect = button.getBoundingClientRect();
        geometry.current.sources.set(index, { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 });
      });
      game.players.forEach(player => {
        const target = document.querySelector(player.seat === game.mySeat ? '[data-daaluu-hand-target]' : `[data-daaluu-collection-seat="${player.seat}"]`)?.getBoundingClientRect();
        if (target) geometry.current.targets.set(player.seat, { x: target.x + target.width / 2, y: target.y + target.height / 2 });
      });
    };
    snapshot();
    window.addEventListener('resize', snapshot);
    window.addEventListener('scroll', snapshot, { capture: true, passive: true });
    return () => { window.removeEventListener('resize', snapshot); window.removeEventListener('scroll', snapshot, true); };
  }, [collecting, distribution.collected, game.mySeat, d.shuffle.roundId]);

  useEffect(() => {
    const last = distribution.lastCollection;
    pending.current = false;
    setPendingIndex(null);
    release();
    if (seen.current.roundId !== d.shuffle.roundId) {
      seen.current = { roundId: d.shuffle.roundId, serial: distribution.collected }; setFlight(null); return;
    }
    if (!last || last.serial <= seen.current.serial) return;
    seen.current.serial = last.serial;
    const source = last.seat === game.mySeat && dropped.current?.index === last.index ? dropped.current.point : geometry.current.sources.get(last.index);
    const target = geometry.current.targets.get(last.seat);
    dropped.current = null;
    if (source && target) setFlight({ serial: last.serial, count: d.shuffle.stackSize,
      from: source, to: target, angle: last.seat === game.mySeat ? 0 : Math.atan2(source.y - target.y, source.x - target.x) * 180 / Math.PI + 90 });
  }, [d.shuffle.roundId, distribution.collected, game.mySeat]);

  useEffect(() => { if (disabled || !collecting) { pending.current = false; setPendingIndex(null); release(); } }, [disabled, collecting]);
  useEffect(() => () => {
    const current = drag.current;
    if (current?.element.hasPointerCapture?.(current.pointerId)) current.element.releasePointerCapture(current.pointerId);
  }, []);

  function collect(index: number, point?: Point) {
    if (!allowed(index) || pending.current) return;
    pending.current = true;
    dropped.current = point ? { index, point } : null;
    setPendingIndex(index);
    onCollect({ type: d.stage === 'selecting' ? 'select' : 'collect', stackHandle: d.shuffle.stacks[index].handle });
  }
  function start(event: PointerEvent<HTMLButtonElement>, index: number) {
    if (!allowed(index) || pending.current || drag.current || event.button !== 0) return;
    suppressClick.current = false;
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { index, pointerId: event.pointerId, element: event.currentTarget, startX: event.clientX, startY: event.clientY };
    setPointer({ index, x: event.clientX, y: event.clientY });
  }
  function finish(event: PointerEvent<HTMLButtonElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const target = document.querySelector('[data-daaluu-hand-target]')?.getBoundingClientRect();
    const wasDragged = Math.hypot(event.clientX - current.startX, event.clientY - current.startY) >= 8;
    suppressClick.current = wasDragged;
    release();
    if (!wasDragged) { collect(current.index); return; }
    if (wasDragged && target && event.clientX >= target.left && event.clientX <= target.right && event.clientY >= target.top && event.clientY <= target.bottom) {
      collect(current.index, { x: event.clientX, y: event.clientY });
    }
  }
  const preview = pointer ?? (pendingIndex !== null && dropped.current ? { ...dropped.current.point, index: pendingIndex } : null);
  const ghost = preview ? <div className="daaluu-collection-ghost" role="img" aria-label={`${mn ? 'Чирж буй өрөг' : 'Dragged stack'} · ${d.shuffle.stackSize} ${mn ? 'мод' : 'tiles'}`} style={{ left: preview.x, top: preview.y }}>
    <BackStack count={d.shuffle.stackSize} /><MixingHand />
  </div> : flight ? <div className="daaluu-collection-flight" key={flight.serial} onAnimationEnd={() => setFlight(null)} style={{
    '--from-x': `${flight.from.x}px`, '--from-y': `${flight.from.y}px`, '--to-x': `${flight.to.x}px`, '--to-y': `${flight.to.y}px`,
    '--hand-rotation': `${flight.angle}deg`,
  } as CSSProperties}><BackStack count={flight.count} /><MixingHand /></div> : null;
  return <>
    {/* Keep source geometry through the final acceptance so its hand flight can finish. */}
    <div className={`daaluu-distribution${collecting ? '' : ' is-inactive'}`} aria-hidden={!collecting}>
      <p className="daaluu-distribution-prompt" role={d.stage === 'selecting' ? 'status' : undefined}>
        {d.stage === 'selecting' ? mn ? 'Эхлэх өргийг сонгоно уу.' : 'Choose a starting stack.' : null}
      </p>
      <div className="daaluu-distribution-ring" role="group" aria-label={mn ? 'Тойрог өрөг' : 'Circular distribution stacks'}>
        {d.shuffle.stacks.map((stack, index) => {
          const angle = -Math.PI / 2 + index * Math.PI * 2 / d.shuffle.stacks.length - game.mySeat * Math.PI * 2 / game.players.length;
          const taken = distribution.startIndex !== null && (index - distribution.startIndex + d.shuffle.stacks.length) % d.shuffle.stacks.length < distribution.collected;
          const lifted = pointer?.index === index || pendingIndex === index;
          return <button ref={element => { if (element) buttons.current.set(index, element); else buttons.current.delete(index); }} key={stack.handle}
            className={`daaluu-distribution-button${taken ? ' is-collected' : ''}${lifted ? ' is-dragging' : ''}${pointer?.index === index ? ' is-pointer-dragging' : ''}`} disabled={!allowed(index) || taken}
            aria-label={`${mn ? 'Өрөг' : 'Stack'} ${index + 1} · ${stack.count} ${mn ? 'мод' : 'tiles'}`}
            aria-describedby={helpId}
            style={{ left: `${50 + Math.cos(angle) * 39}%`, top: `${45 + Math.sin(angle) * 34}%` }}
            onPointerDown={event => start(event, index)} onPointerMove={event => {
              const current = drag.current;
              if (current?.pointerId === event.pointerId) setPointer({ index: current.index, x: event.clientX, y: event.clientY });
            }} onPointerUp={finish} onPointerCancel={release} onLostPointerCapture={release}
            onKeyDown={event => {
              if (event.key === 'Escape') release();
              if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                event.preventDefault(); collect(index);
              }
            }} onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } collect(index); }}>
            {!lifted && !taken && <><BackStack count={stack.count} /><span className="daaluu-stack-number">{index + 1}</span></>}
          </button>;
        })}
      </div>
      <p id={helpId} className="daaluu-deal-help">{mn ? 'Өргийг товших эсвэл Enter, зай товчоор сонгоно.' : 'Select a stack with a click, Enter, or Space.'}</p>
    </div>
    {ghost && createPortal(ghost, document.body)}
  </>;
}
